<template>
  <Dialog :visible="visible" modal :closable="false"
    :style="{ width: '50rem' }" class="mx-4 black-bg">
    <template #header>
      <div class="inline-flex items-center justify-center gap-2">
        <font-awesome-icon class="text-2xl text-[var(--color-orange)]"
          icon="fa-regular fa-lightbulb"/>
        <span class="font-bold whitespace-nowrap text-xl">
          {{ $t('FeedbackOverlayComponent.devTipFeedback', {text: featureNameDevTip}) }}
        </span>
      </div>
      <button @click="$emit('update:visible', false)"
        class="text-gray-500 hover:text-gray-700 absolute top-4 right-4">
        <font-awesome-icon icon="fa-times" class="text-xl"/>
      </button>
    </template>
    <form action="#" @click.stop>
      <p><b>{{ $t('FeedbackOverlayComponent.ourTip') }}</b>&nbsp; &nbsp;
        <span v-html="getDevTipContent"></span>
      </p>
      <div class="flex flex-col sm:flex-row items-center justify-start
         gap-4 place-items-center px-12 py-4">
        <p>{{ $t('FeedbackOverlayComponent.wasTipCompleted') }}</p>
        <div class="flex flex-row gap-4 flex-wrap">
          <Button
            :class="getTipCompleted === true
              ? 'btn-primary-accent inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'
              : 'btn-secondary inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'"
            @click.stop="tipCompleted = true" :disabled="this.data?.done === true"
            :label="$t('FeedbackOverlayComponent.yes')" icon="pi pi-thumbs-up-fill" unstyled/>
          <Button
            :class="getTipCompleted === false
              ? 'btn-primary-accent inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'
              : 'btn-secondary inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'"
            @click.stop="tipCompleted = false" :disabled="this.data?.done === true"
            :label="$t('FeedbackOverlayComponent.notYet')" icon="pi pi-thumbs-down-fill" unstyled/>
        </div>
      </div>
      <p><b>{{ $t('FeedbackOverlayComponent.yourFeedback') }}</b>
        <span v-if="hasFeeback" class="text-sm text-gray-500 ml-2">
          {{ $t('FeedbackOverlayComponent.previousFeedbackUpdated') }}
        </span>

      </p>

      <div class="flex flex-col sm:flex-row items-center justify-start
         gap-4 place-items-center px-12 py-4">
        <p>{{ $t('FeedbackOverlayComponent.wasTipHelpful') }}</p>
        <div class="flex flex-row gap-4 flex-wrap">
          <Button
            :class="getHelpful === true
              ? 'btn-primary-accent inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'
              : 'btn-secondary inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'"
            @click.stop="helpful = true" :disabled="this.data?.done === true"
            :label="$t('FeedbackOverlayComponent.yesSuper')" icon="pi pi-thumbs-up-fill" unstyled />
          <Button
            :class="getHelpful === false
              ? 'btn-primary-accent inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'
              : 'btn-secondary inline-flex items-center justify-center w-auto py-2 px-4 text-sm sm:text-base'"
            @click.stop="helpful = false" :disabled="this.data?.done === true"
            :label="$t('FeedbackOverlayComponent.notReally')" icon="pi pi-thumbs-down-fill" unstyled />
        </div>
      </div>
      <div class="my-2" @click.stop>
        <Textarea v-model="text" maxlength="5000" class="text-base h-40 w-full
            disabled:bg-[rgba(219, 219, 219, 0.9)]"
          :placeholder="hasFeeback ? $t('FeedbackOverlayComponent.updateFeedback') :
           $t('FeedbackOverlayComponent.placeholderText')"
                         :disabled="this.data?.done === true">
        </Textarea>
      </div>
      <div class="flex justify-end mt-4" @click.stop>
        <Button class="btn-primary-accent inline-flex items-center justify-center w-auto h-10 px-4 text-sm sm:text-base"
          :disabled="buttonDisabled" @click.prevent="submitFeedback" unstyled>
          {{ $t('FeedbackOverlayComponent.sendFeedback') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script>
import { Button, Dialog, Textarea } from 'primevue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'FeedbackOverlayComponent',
  props: {
    data: Object,
    visible: {
      type: Boolean,
      default: false,
    },
  },
  components: { Dialog, Textarea, Button },
  data() {
    return {
      text: this.data?.feedback_text || '',
      helpful: this.data?.helpful,
      buttonDisabled: this.data?.done === true, // Disable if tip is done
      tipCompleted: this.data?.done || false,
      hasFeeback: this.data?.feedback_text || this.data?.helpful !== undefined,
    };
  },
  mounted() {
    // this.scrollToElement();
    this.clearForm();
  },
  watch: {
    data: {
      handler(newData) {
        if (newData) {
          this.updateFormData(newData);
        }
      },
      deep: true,
      immediate: true,
    },
    'data.done': {
      handler(newStatus, oldStatus) {
        if (newStatus !== oldStatus) {
          this.updateFormData(this.data);
        }
      },
      immediate: true,
    },
  },
  computed: {
    getHelpful() {
      return this.helpful;
    },
    getLocale() {
      return this.$i18n.locale;
    },
    getTipCompleted() {
      return this.tipCompleted;
    },
    getDevTipContent() {
      // Handle different notification data structures
      if (this.data?.content?.text?.[this.getLocale]) {
        // New notification structure
        return this.data.content.text[this.getLocale];
      }
      if (this.data?.text?.[this.getLocale]) {
        // Old notification structure
        return this.data.text[this.getLocale];
      }
      if (this.data?.text) {
        // Direct text property
        return this.data.text;
      }
      return '-';
    },
    featureNameDevTip() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'developmentTip',
        this.$t('FallbackTexts.developmentTip'),
        this.$i18n?.locale,
      );
    },
  },
  methods: {
    async submitFeedback() {
      try {
        if (!this.inputCheck()) {
          return;
        }
        const tipId = this.getTipId();
        if (!tipId) {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No tip ID found. Cannot submit feedback.',
            life: 3000,
          });
          return;
        }

        this.buttonDisabled = true;
        await this.$store.dispatch('devTips/markDevTipAsViewed', tipId);
        await this.$store.dispatch('submitFeedback', {
          id: tipId,
          feedback: {
            positive: this.helpful,
            feedback_text: this.text,
          },
          status_done: this.tipCompleted,
        });

        // Update local state to reflect the new status
        this.updateFormData({
          ...this.data,
          feedback_text: this.text,
          helpful: this.helpful,
          done: this.tipCompleted,
        });

        this.buttonDisabled = false;
        // this.$store.commit('removeFrontendOverlay', this.data.id);
        this.$emit('update:visible', false);
        this.$emit('confirmed');
      } catch (error) {
        this.buttonDisabled = false;
        this.$toast.add({
          severity: 'error',
          summary: this.$t('FeedbackOverlayComponent.errorCouldNotFeedback'),
          detail: '',
          life: 3000,
        });
      }
    },
    getTipId() {
      // Handle different notification data structures for tip ID
      if (this.data?.content?.tipId) {
        return this.data.content.tipId;
      }
      if (this.data?.content?.id) {
        return this.data.content.id;
      }
      if (this.data?.tip_id) {
        return this.data.tip_id;
      }
      if (this.data?.tipId) {
        return this.data.tipId;
      }
      if (this.data?.id) {
        return this.data.id;
      }
      console.error('No tip ID found in data:', this.data);
      return null;
    },
    inputCheck() {
      // Allow feedback even if helpful is not selected
      // Only require tip completion status
      if (this.tipCompleted !== undefined) {
        return true;
      }
      this.$toast.add({
        severity: 'error',
        summary: this.$t('FeedbackOverlayComponent.errorRequired'),
        detail: 'Please indicate if the tip was completed.',
        life: 3000,
      });
      return false;
    },
    updateFormData(data) {
      // Update form data when data prop changes
      this.text = data?.feedback_text || '';
      this.helpful = data?.helpful;
      this.tipCompleted = data?.done || false;
      this.buttonDisabled = data?.done === true;
      this.hasFeeback = data?.feedback_text || data?.helpful !== undefined;
    },
    refreshFormData() {
      this.updateFormData(this.data);
    },
    clearForm() {
      // Load existing feedback data if available
      this.text = this.data?.feedback_text || '';
      this.helpful = this.data?.helpful;
      this.tipCompleted = this.data?.done || false;
      this.buttonDisabled = this.data?.done === true;
      this.hasFeeback = this.data?.feedback_text || this.data?.helpful !== undefined;
    },
    scrollToElement() {
      const el = this.$refs.devTipEl;
      if (el) {
        // Use el.scrollIntoView() to instantly scroll to the element
        el.scrollIntoView({ behavior: 'smooth' });
      }
    },

  },
};
</script>
